/**
 * SubconPayrollService — reads and writes for the SUBCONTRACTORS · Payroll
 * module's own four lists, and nothing else.
 *
 * WHAT IT NEVER TOUCHES. Payroll's quantities come from `iSyn2_QcScans`,
 * `iSyn2_DispatchSlips` and `iSyn_WIPs`, all of which it READS through the
 * services that already own them (QcService, DispatchService,
 * ProductionGridService). It writes none of them, and it is not a backfill:
 * every row it creates is a payslip this app produced, in a list nothing else
 * reads. (`backfillGuard.ts` refuses these lists precisely because nobody has
 * signed off on a bulk import of workbook history into them — that is a
 * separate, deliberate change with its own failing test, not this one.)
 *
 * $SELECT FALLBACK, THE HOUSE RULE. SharePoint rejects the WHOLE `$select`
 * when it names one column a list does not have — the read 400s rather than
 * degrading — so a column added after a list was first provisioned would take
 * out every reader until "Add missing columns" is run. Each read therefore
 * tries the fullest select first and falls back one generation at a time, the
 * way ProductionGridService.readWipRows, DispatchService and
 * WorkcentersService.listCutReadyScans all do. The fallback SAYS SO in the
 * console rather than swallowing it: a payslip silently missing its lines is
 * a payslip that pays nothing.
 *
 * DATES ARE STORED AS DATES, READ AS ISO DAYS. SharePoint hands back a full
 * timestamp for a datetime column; everything in this module compares
 * `YYYY-MM-DD` strings, so the boundary is crossed once, here.
 */
import {
  NON_BPI_DETAILS_KEY, NON_BPI_DEFAULTS_KEY, parseNonBpiDetails, parseNonBpiDefaults, mergeNonBpiDetail,
  type INonBpiDetail, type INonBpiDefaults, type StoredNonBpiDetails,
} from "../../modules/subcontractors/nonBpiDetails";
import { createSharePointClient } from "./spClient";
import { MFG_SITE_URL } from "./mfgSite";
import { assertBackfillTarget } from "./backfillGuard";
import {
  BIZLINK_TEMPLATE_KEY, BIZLINK_TEMPLATE_MAX_BYTES, base64ToBytes, bytesToBase64, describeTemplate,
} from "../../modules/subcontractors/biffTemplate";
import {
  SUBCON_PAYOUTS_LIST, SUBCON_PROFILES_LIST, SUBCON_RATECARD_LIST,
  SUBCON_SETTINGS_LIST,
} from "./SubconPayrollProvisioningService";
import type { IPayrollProfile } from "../../modules/subcontractors/payrollProfile";
import { DEFAULT_POSITION, paymentMode } from "../../modules/subcontractors/payrollProfile";
import { PAYROLL_MASTERLIST } from "../../modules/subcontractors/payrollMasterlist";
import type { IPayout, PayoutStatus } from "../../modules/subcontractors/payout";
import { totalsOf } from "../../modules/subcontractors/payout";
import type { IRateRow, RateGroup, RateMatch } from "../../modules/subcontractors/rateCard";
import { RATE_CARD } from "../../modules/subcontractors/rateCard";
import {
  isStorableLogo, PAYSLIP_LOGO_KEY, PAYSLIP_FORM_KEY, parseStoredForm, type IPayslipForm,
} from "../../modules/subcontractors/payslipBranding";
import { PANTS_MAP_KEY, parseStoredPantsMap, type StoredPantsMap } from "../../modules/subcontractors/pantsMap";
import { WIP_PANTS_KEY, parseStoredWipPants, type StoredWipPants } from "../../modules/subcontractors/wipPants";
import {
  LINE_EDITS_KEY, parseStoredLineEdits, mergeLineEdit, clearPayoutEdits, type StoredLineEdits,
} from "../../modules/subcontractors/lineEdits";
import {
  KEY_PAYABLE_KEY, parseStoredKeyPayable, mergePayable, type IPayablePass, type StoredKeyPayable,
} from "../../modules/subcontractors/payableQty";
import { SEWER_ALIASES_KEY, parseStoredAliases, type SewerAliases } from "../../modules/subcontractors/sewerAlias";
import {
  KEY_OVERRIDES_KEY, parseStoredKeyOverrides, type KeyOverride, type StoredKeyOverrides,
} from "../../modules/subcontractors/keyOverrides";
import {
  WORKBOOK_PAID_KEYS_COUNT_KEY, WORKBOOK_PAID_KEYS_PREFIX,
  chunkKeys, mergeKeys, parseKeyChunk,
} from "../../modules/subcontractors/workbookPaidKeys";
import {
  WIP_PLANS_KEY, parseStoredWipPlans, type StoredWipPlans, type TagPlan,
} from "../../modules/subcontractors/wipPlan";
import { isDemo, PAYROLL_WRITE_METHODS } from "../../modules/subcontractors/demo/demoMode";
import { demoPayrollStore } from "../../modules/subcontractors/demo/demoStore";
import type {
  BirSide, IPayoutAdditions, IPayoutDeductions, IPayoutLine,
} from "../../modules/subcontractors/payrollMath";
import { DEFAULT_ADDITIONS, NO_DEDUCTIONS } from "../../modules/subcontractors/payrollMath";

const PROFILE_SELECT =
  "Id,ledgerKey,fullName,contractorId,bir,companyName,tin,bankName,bankAccount," +
  "modeOfPayment,location,section,payoutSchedule,dateStarted,cellphone,email,position,active";

const SETTING_SELECT = "Id,settingKey,settingText";

const RATE_SELECT = "Id,rateKey,code,rateGroup,item,rate,matchKind,designCodes,sortOrder,active";

/** The payout read, fullest first. `voidReason` and the void stamps arrived
 *  after the first cut of the list; a tenant provisioned before them reads
 *  the core select and loses only the reason a payout was voided. */
const PAYOUT_CORE =
  "Id,payoutRef,sewer,contractorId,contractorName,companyName,tin,bir,section," +
  "periodStart,periodEnd,dateIssued,modeOfPayment,bankName,bankAccount,schedPayout," +
  "linesJson,additionsJson,deductionsJson,qtyTotal,workTotal,netAmount,totalAmount," +
  "payoutStatus,remarks,createdBy,createdAt,issuedBy,issuedAt,paidBy,paidAt";
const PAYOUT_SELECT = `${PAYOUT_CORE},voidedBy,voidedAt,voidReason`;

/**
 * The same read WITHOUT `linesJson`, which is the whole payslip's work lines
 * as a JSON string and by far the biggest column on the list.
 *
 * Mikko, 2026-09-18: "im experiencing laging to Subcontractor module." Paid
 * history read every payout ever written — five thousand imported records and
 * counting — with its lines attached, to paint twenty-five rows of
 * references and amounts. The lines are megabytes; the rest is a table.
 *
 * The amounts a summary row shows are the list's OWN columns
 * (`qtyTotal`/`totalAmount`), which exist for exactly this. Lines are then
 * read for the handful of payouts that need them — the page being looked at,
 * and the batch being printed (see readPayoutLinesFor).
 */
const PAYOUT_SUMMARY_CORE = PAYOUT_CORE.replace("linesJson,", "");
const PAYOUT_SUMMARY_SELECT = `${PAYOUT_SUMMARY_CORE},voidedBy,voidedAt,voidReason`;

/** A payout as a TABLE ROW: no work lines, and the list's own totals. */
export interface IPayoutSummary {
  /** Everything but the lines. `lines` is empty — never sum it. */
  payout: IPayout;
  /** The list's `totalAmount` column, for the drift check. */
  snapshotTotal: number | null;
  /** The stored columns a row shows, so nothing is computed from no lines. */
  stored: { qtyTotal: number; workTotal: number; netAmount: number; totalAmount: number };
}

const isoDay = (v: unknown): string => String(v ?? "").slice(0, 10);
const text = (v: unknown): string => String(v ?? "").trim();
const numOr = (v: unknown, fallback = 0): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

/** Parse a JSON column, falling back rather than throwing — a payout whose
 *  lines will not parse is still a payout with a reference and an amount, and
 *  hiding the whole row would hide the problem too. */
function parseJson<T>(raw: unknown, fallback: T, what: string): T {
  const s = text(raw);
  if (!s) return fallback;
  try {
    return JSON.parse(s) as T;
  } catch (e) {
    console.warn(`[SubconPayrollService] could not parse ${what} — showing the row without it`, e);
    return fallback;
  }
}

export class SubconPayrollService {
  private readonly sp = createSharePointClient(MFG_SITE_URL);
  private readonly types = new Map<string, string>();

  private async type(list: string): Promise<string> {
    const hit = this.types.get(list);
    if (hit) return hit;
    const r = await this.sp.raw<{ d: { ListItemEntityTypeFullName: string } }>(
      `/_api/web/lists/getbytitle('${encodeURIComponent(list)}')?$select=ListItemEntityTypeFullName`);
    this.types.set(list, r.d.ListItemEntityTypeFullName);
    return r.d.ListItemEntityTypeFullName;
  }

  // ── Profiles ──────────────────────────────────────────────────────────

  private mapProfile(r: Record<string, unknown>): IPayrollProfile {
    return {
      spId: r.Id as number,
      ledgerKey: text(r.ledgerKey).toUpperCase(),
      fullName: text(r.fullName),
      contractorId: text(r.contractorId),
      bir: text(r.bir).toUpperCase() === "BIR" ? "BIR" : "NON-BIR",
      companyName: text(r.companyName) || undefined,
      tin: text(r.tin) || undefined,
      bankName: text(r.bankName) || undefined,
      bankAccount: text(r.bankAccount) || undefined,
      modeOfPayment: paymentMode(text(r.modeOfPayment)),
      location: text(r.location) || undefined,
      section: text(r.section) || undefined,
      schedule: text(r.payoutSchedule) || undefined,
      dateStarted: isoDay(r.dateStarted) || null,
      cellphone: text(r.cellphone) || undefined,
      email: text(r.email) || undefined,
      position: text(r.position) || undefined,
      // Absent reads as active: a profile created through the list UI without
      // ticking a box is a real subcontractor, not a retired one.
      active: r.active !== false,
    };
  }

  public async listProfiles(): Promise<IPayrollProfile[]> {
    const rows = await this.sp.getListItems<Record<string, unknown>>(SUBCON_PROFILES_LIST, {
      select: PROFILE_SELECT, top: 500,
    });
    return rows.map(r => this.mapProfile(r)).filter(p => !!p.ledgerKey);
  }

  private profileBody(p: IPayrollProfile, type: string): Record<string, unknown> {
    return {
      __metadata: { type },
      Title: p.ledgerKey,
      ledgerKey: p.ledgerKey.toUpperCase(),
      fullName: p.fullName,
      contractorId: p.contractorId,
      bir: p.bir,
      companyName: p.companyName ?? "",
      tin: p.tin ?? "",
      bankName: p.bankName ?? "",
      bankAccount: p.bankAccount ?? "",
      modeOfPayment: p.modeOfPayment,
      location: p.location ?? "",
      section: p.section ?? "",
      payoutSchedule: p.schedule ?? "",
      dateStarted: p.dateStarted || null,
      cellphone: p.cellphone ?? "",
      email: p.email ?? "",
      position: p.position ?? "",
      active: p.active !== false,
    };
  }

  public async createProfile(p: IPayrollProfile): Promise<IPayrollProfile> {
    const type = await this.type(SUBCON_PROFILES_LIST);
    const created = await this.sp.createItem<{ Id: number }>(SUBCON_PROFILES_LIST, this.profileBody(p, type));
    return { ...p, spId: created?.Id };
  }

  public async updateProfile(spId: number, p: IPayrollProfile): Promise<void> {
    const type = await this.type(SUBCON_PROFILES_LIST);
    await this.sp.updateItem(SUBCON_PROFILES_LIST, spId, this.profileBody(p, type));
  }

  /**
   * Retire rather than delete — the same rule SubconRegistryService follows,
   * for a sharper reason here: a profile's payout history joins on its ledger
   * key, and deleting the profile does not delete the money that moved.
   */
  public async setProfileActive(spId: number, active: boolean): Promise<void> {
    const type = await this.type(SUBCON_PROFILES_LIST);
    await this.sp.updateItem(SUBCON_PROFILES_LIST, spId, { __metadata: { type }, active });
  }

  /**
   * Create a payroll profile for every masterlist contractor that does not
   * already have one, and touch nothing that does.
   *
   * ADDITIVE, NEVER A RE-SYNC. A profile that exists is skipped outright —
   * not merged, not refreshed. The seed only knows what the workbook knew in
   * September 2026; a bank account typed into the app since then is newer
   * than anything here, and "helpfully" overwriting it would send a payment
   * to a closed account. So the rule is that this fills gaps and the app owns
   * everything after.
   *
   * Bank details and TINs are deliberately absent from the seed and stay
   * absent here — see payrollMasterlist.ts. Every profile it creates is
   * payable-for-a-payslip (it has a contractor ID) and not yet payable-by-
   * bank-transfer, which the Directory flags per row.
   *
   * Per-row failures are collected rather than thrown: 93 creates over
   * SharePoint REST is long enough that one throttled row must not strand the
   * other 92, and a partial seed is fixed by running it again.
   */
  public async seedProfiles(
    onProgress?: (m: string) => void
  ): Promise<{ created: number; skipped: number; failed: { key: string; error: string }[] }> {
    const existing = await this.listProfiles();
    const have = new Set(existing.map(p => p.ledgerKey.toUpperCase()));
    const type = await this.type(SUBCON_PROFILES_LIST);
    let created = 0;
    let skipped = 0;
    const failed: { key: string; error: string }[] = [];

    for (const e of PAYROLL_MASTERLIST) {
      const key = e.key.toUpperCase();
      if (have.has(key)) { skipped++; continue; }
      const profile: IPayrollProfile = {
        ledgerKey: key,
        fullName: e.name,
        contractorId: e.id,
        bir: e.bir,
        companyName: e.company,
        section: e.section,
        location: e.section,
        schedule: e.schedule,
        dateStarted: e.started ?? null,
        position: DEFAULT_POSITION[e.bir],
        // The one field the seed cannot know. BANK TRANSFER is what nine
        // payouts in ten use, and the Directory flags the missing account
        // rather than letting it reach the bank file.
        modeOfPayment: "BANK TRANSFER",
        active: true,
      };
      try {
        await this.sp.createItem(SUBCON_PROFILES_LIST, this.profileBody(profile, type));
        created++;
        if (created % 10 === 0) onProgress?.(`Created ${created} profiles…`);
      } catch (err) {
        failed.push({ key, error: err instanceof Error ? err.message : String(err) });
      }
    }
    onProgress?.(
      `${created} created, ${skipped} already there` +
      (failed.length > 0 ? `, ${failed.length} failed — run it again to retry those` : ""));
    return { created, skipped, failed };
  }

  /**
   * Write bank details onto profiles that already exist.
   *
   * A NARROW WRITE, not a full profile save. Only the four fields the import
   * proposes are sent, so a name or section edited in the app since the
   * workbook was last touched is not quietly rolled back by an import that
   * was never about those fields.
   *
   * Per-row failures are collected rather than thrown, for the same reason
   * seedProfiles collects them: one throttled row must not strand the rest,
   * and re-running is safe because a field that already matches produces no
   * proposal the second time.
   */
  public async applyBankDetails(
    updates: readonly {
      spId: number;
      bankName?: string;
      bankAccount?: string;
      tin?: string;
      modeOfPayment?: string;
    }[],
    onProgress?: (m: string) => void
  ): Promise<{ updated: number; failed: { spId: number; error: string }[] }> {
    const type = await this.type(SUBCON_PROFILES_LIST);
    let updated = 0;
    const failed: { spId: number; error: string }[] = [];
    for (const u of updates) {
      const body: Record<string, unknown> = { __metadata: { type } };
      if (u.bankName !== undefined) body.bankName = u.bankName;
      if (u.bankAccount !== undefined) body.bankAccount = u.bankAccount;
      if (u.tin !== undefined) body.tin = u.tin;
      if (u.modeOfPayment !== undefined) body.modeOfPayment = u.modeOfPayment;
      try {
        await this.sp.updateItem(SUBCON_PROFILES_LIST, u.spId, body);
        updated++;
        if (updated % 10 === 0) onProgress?.(`Updated ${updated}/${updates.length}…`);
      } catch (e) {
        failed.push({ spId: u.spId, error: e instanceof Error ? e.message : String(e) });
      }
    }
    onProgress?.(`${updated} updated` + (failed.length > 0 ? `, ${failed.length} failed — run it again` : ""));
    return { updated, failed };
  }

  /**
   * Write ONLY the payout day onto profiles that already exist — the Directory's
   * "Apply official schedule". Narrow on purpose, like applyBankDetails: a bank
   * account or section edited in the app is not touched. Safe to run again
   * (a day that already matches is not proposed).
   */
  public async applySchedule(
    updates: readonly { spId: number; schedule: string }[],
    onProgress?: (m: string) => void
  ): Promise<{ updated: number; failed: { spId: number; error: string }[] }> {
    const type = await this.type(SUBCON_PROFILES_LIST);
    let updated = 0;
    const failed: { spId: number; error: string }[] = [];
    for (const u of updates) {
      try {
        await this.sp.updateItem(SUBCON_PROFILES_LIST, u.spId, { __metadata: { type }, payoutSchedule: u.schedule });
        updated++;
        if (updated % 10 === 0) onProgress?.(`Updated ${updated}/${updates.length}…`);
      } catch (e) {
        failed.push({ spId: u.spId, error: e instanceof Error ? e.message : String(e) });
      }
    }
    onProgress?.(`${updated} updated` + (failed.length > 0 ? `, ${failed.length} failed — run it again` : ""));
    return { updated, failed };
  }

  // ── Rate card ─────────────────────────────────────────────────────────

  private mapRate(r: Record<string, unknown>): IRateRow {
    const designs = parseJson<string[]>(r.designCodes, [], "designCodes");
    const rawRate = r.rate;
    return {
      rateKey: text(r.rateKey),
      code: text(r.code),
      group: text(r.rateGroup) as RateGroup,
      item: text(r.item),
      // Null and 0 are different: an ADMIN TASKS row has no unit rate at all,
      // and showing it as ₱0.00 invites someone to "fix" it to zero.
      rate: rawRate === null || rawRate === undefined || rawRate === "" ? null : numOr(rawRate),
      match: (text(r.matchKind) === "design" ? "design" : "manual") as RateMatch,
      designs,
      sortOrder: numOr(r.sortOrder),
    };
  }

  /**
   * The live rate card, or the shipped one when the list is empty or
   * unreadable.
   *
   * FALLING BACK IS THE RIGHT ANSWER HERE and not everywhere: the shipped
   * card is a faithful copy of the workbook the clerk is holding, so a payout
   * priced from it is priced correctly — it is only stale if somebody has
   * since edited the list. The caller is told which it got so the screen can
   * say so.
   */
  public async getRateCard(): Promise<{ card: IRateRow[]; source: "list" | "shipped"; error?: string }> {
    try {
      const rows = await this.sp.getListItems<Record<string, unknown>>(SUBCON_RATECARD_LIST, {
        select: RATE_SELECT, top: 300,
      });
      const card = rows
        .filter(r => r.active !== false)
        .map(r => this.mapRate(r))
        .filter(r => !!r.rateKey)
        .sort((a, b) => a.sortOrder - b.sortOrder);
      if (card.length === 0) return { card: [...RATE_CARD], source: "shipped" };
      // The list is a COPY of the shipped card made when it was seeded, so a design the shipped card learned
      // afterwards (WU01 to WU05 → NU, 2026-10-20) never reached it. Lend each list row the shipped designs
      // for the same code that no other row of the list already claims; a person's own edit still wins.
      const claimed = new Set(card.flatMap(r => r.designs));
      for (const row of card) {
        const shipped = RATE_CARD.find(s => s.code === row.code && s.group === row.group);
        if (!shipped) continue;
        const add = shipped.designs.filter(d => !claimed.has(d));
        if (add.length > 0) { row.designs = [...row.designs, ...add]; add.forEach(d => claimed.add(d)); }
      }
      return { card, source: "list" };
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      console.warn("[SubconPayrollService] rate card unreadable — using the shipped card", e);
      return { card: [...RATE_CARD], source: "shipped", error: msg };
    }
  }

  /**
   * Fill an empty rate-card list from the shipped card.
   *
   * Refuses outright if the list already holds anything. Re-seeding a card
   * somebody has edited would silently restore the workbook's rates over
   * theirs, and the row that changed is the one nobody would check.
   */
  public async seedRateCard(onProgress?: (m: string) => void): Promise<number> {
    const existing = await this.sp.getListItems<{ Id: number }>(SUBCON_RATECARD_LIST, {
      select: "Id", top: 5, singlePage: true,
    });
    if (existing.length > 0) {
      onProgress?.("Rate card already has rows — left untouched");
      return 0;
    }
    const type = await this.type(SUBCON_RATECARD_LIST);
    let n = 0;
    for (const r of RATE_CARD) {
      await this.sp.createItem(SUBCON_RATECARD_LIST, {
        __metadata: { type },
        Title: r.rateKey,
        rateKey: r.rateKey,
        code: r.code,
        rateGroup: r.group,
        item: r.item,
        rate: r.rate,
        matchKind: r.match,
        designCodes: JSON.stringify(r.designs),
        sortOrder: r.sortOrder,
        active: true,
      });
      n++;
      if (n % 10 === 0) onProgress?.(`Seeded ${n}/${RATE_CARD.length} rate rows…`);
    }
    onProgress?.(`Seeded ${n} rate rows`);
    return n;
  }

  public async updateRate(spId: number, patch: { rate?: number | null; item?: string; active?: boolean; designs?: string[] }): Promise<void> {
    const type = await this.type(SUBCON_RATECARD_LIST);
    const body: Record<string, unknown> = { __metadata: { type } };
    if (patch.rate !== undefined) body.rate = patch.rate;
    if (patch.item !== undefined) body.item = patch.item;
    if (patch.active !== undefined) body.active = patch.active;
    if (patch.designs !== undefined) body.designCodes = JSON.stringify(patch.designs);
    await this.sp.updateItem(SUBCON_RATECARD_LIST, spId, body);
  }

  /** Rate rows with their list ids, for the editor — `getRateCard` drops
   *  them because nothing else needs one. */
  public async listRateRows(): Promise<{ spId: number; row: IRateRow; active: boolean }[]> {
    const rows = await this.sp.getListItems<Record<string, unknown>>(SUBCON_RATECARD_LIST, {
      select: RATE_SELECT, top: 300,
    });
    return rows
      .map(r => ({ spId: r.Id as number, row: this.mapRate(r), active: r.active !== false }))
      .sort((a, b) => a.row.sortOrder - b.row.sortOrder);
  }

  // ── Payouts ───────────────────────────────────────────────────────────

  private mapPayout(r: Record<string, unknown>): IPayout {
    const status = text(r.payoutStatus) as PayoutStatus;
    return {
      spId: r.Id as number,
      payoutRef: text(r.payoutRef),
      sewer: text(r.sewer).toUpperCase(),
      contractorId: text(r.contractorId),
      contractorName: text(r.contractorName),
      companyName: text(r.companyName) || undefined,
      tin: text(r.tin) || undefined,
      bir: (text(r.bir).toUpperCase() === "BIR" ? "BIR" : "NON-BIR") as BirSide,
      section: text(r.section) || undefined,
      periodStart: isoDay(r.periodStart),
      periodEnd: isoDay(r.periodEnd),
      dateIssued: isoDay(r.dateIssued),
      modeOfPayment: paymentMode(text(r.modeOfPayment)),
      bankName: text(r.bankName) || undefined,
      bankAccount: text(r.bankAccount) || undefined,
      schedPayout: text(r.schedPayout) || undefined,
      lines: parseJson<IPayoutLine[]>(r.linesJson, [], `lines of ${text(r.payoutRef)}`),
      additions: parseJson<IPayoutAdditions>(r.additionsJson, DEFAULT_ADDITIONS, `additions of ${text(r.payoutRef)}`),
      deductions: parseJson<IPayoutDeductions>(r.deductionsJson, NO_DEDUCTIONS, `deductions of ${text(r.payoutRef)}`),
      // An unrecognised status reads as Draft: nothing is owed on a Draft, so
      // a corrupt value cannot conjure a payable row out of nowhere.
      status: (["Draft", "Issued", "Paid", "Void"] as string[]).includes(status) ? status : "Draft",
      remarks: text(r.remarks) || undefined,
      createdBy: text(r.createdBy) || undefined,
      createdAt: text(r.createdAt) || undefined,
      issuedBy: text(r.issuedBy) || undefined,
      issuedAt: text(r.issuedAt) || undefined,
      paidBy: text(r.paidBy) || undefined,
      paidAt: text(r.paidAt) || undefined,
      voidedBy: text(r.voidedBy) || undefined,
      voidedAt: text(r.voidedAt) || undefined,
      voidReason: text(r.voidReason) || undefined,
    };
  }

  /** The stored snapshot amount, kept beside the payout so the Records
   *  screen can report drift (see payout.driftOf). */
  private snapshotOf(r: Record<string, unknown>): number | null {
    const v = r.totalAmount;
    return v === null || v === undefined || v === "" ? null : numOr(v);
  }

  private async readPayouts(select: string, filter?: string) {
    return this.sp.getListItems<Record<string, unknown>>(SUBCON_PAYOUTS_LIST, {
      select, filter, top: 2000, orderby: "periodEnd desc",
    });
  }

  /**
   * Payouts WITHOUT their work lines, for a table.
   *
   * `payout.lines` is EMPTY here and must never be summed — `stored` carries
   * the list's own totals, which is what a row shows. Anything that needs the
   * real lines (a drift check, a printed payslip) asks for them by id.
   */
  public async listPayoutSummaries(filter?: string): Promise<IPayoutSummary[]> {
    let rows: Record<string, unknown>[];
    try {
      rows = await this.readPayouts(PAYOUT_SUMMARY_SELECT, filter);
    } catch (e) {
      console.warn(
        "[SubconPayrollService] payout summary read without voidedBy/voidedAt/voidReason — a voided payout " +
        "cannot show why. Use \"Add missing columns\" on the Payroll settings screen.", e);
      rows = await this.readPayouts(PAYOUT_SUMMARY_CORE, filter);
    }
    return rows.map(r => ({
      payout: this.mapPayout(r),
      snapshotTotal: this.snapshotOf(r),
      stored: {
        qtyTotal: numOr(r.qtyTotal),
        workTotal: numOr(r.workTotal),
        netAmount: numOr(r.netAmount),
        totalAmount: numOr(r.totalAmount),
      },
    }));
  }

  /**
   * The work lines of particular payouts, by list id.
   *
   * Chunked, because the filter is `Id eq … or Id eq …` and a URL has a
   * length. Forty at a time covers a page of a table and a printed batch in
   * one or two requests.
   */
  public async readPayoutLinesFor(spIds: readonly number[]): Promise<Map<number, IPayoutLine[]>> {
    const out = new Map<number, IPayoutLine[]>();
    const ids = [...new Set(spIds.filter(n => Number.isFinite(n)))];
    for (let i = 0; i < ids.length; i += 40) {
      const chunk = ids.slice(i, i + 40);
      const rows = await this.sp.getListItems<Record<string, unknown>>(SUBCON_PAYOUTS_LIST, {
        select: "Id,payoutRef,linesJson",
        filter: chunk.map(id => `Id eq ${id}`).join(" or "),
        top: 200,
      });
      for (const r of rows) {
        out.set(r.Id as number, parseJson<IPayoutLine[]>(r.linesJson, [], `lines of ${text(r.payoutRef)}`));
      }
    }
    return out;
  }

  /**
   * Payouts, newest period first, with their stored amount alongside.
   *
   * `filter` is an OData clause the caller builds — the Records screen scopes
   * by period so a tenant three years into this is not reading every payslip
   * it has ever written to show one week.
   */
  public async listPayouts(filter?: string): Promise<{ payout: IPayout; snapshotTotal: number | null }[]> {
    let rows: Record<string, unknown>[];
    try {
      rows = await this.readPayouts(PAYOUT_SELECT, filter);
    } catch (e) {
      console.warn(
        "[SubconPayrollService] payout read without voidedBy/voidedAt/voidReason — a voided payout cannot " +
        "show why. Use \"Add missing columns\" on the Payroll settings screen.", e);
      rows = await this.readPayouts(PAYOUT_CORE, filter);
    }
    return rows.map(r => ({ payout: this.mapPayout(r), snapshotTotal: this.snapshotOf(r) }));
  }

  /** Every payout covering a working period — what the run screen checks
   *  before saving, so re-running a week updates rather than duplicates. */
  public async listPayoutsForPeriod(periodEndIso: string): Promise<IPayout[]> {
    const rows = await this.listPayouts(`periodEnd eq datetime'${periodEndIso}T00:00:00Z'`);
    return rows.map(r => r.payout);
  }

  /**
   * Payouts whose period ended on or after a date — what the run screen
   * needs (the recent paid scan keys and this period's drafts), without the
   * fifteen months of imported history behind them. Records reads the lot.
   */
  public async listPayoutsSince(periodEndIso: string): Promise<IPayout[]> {
    const rows = await this.listPayouts(`periodEnd ge datetime'${periodEndIso}T00:00:00Z'`);
    return rows.map(r => r.payout);
  }

  private payoutBody(p: IPayout, type: string): Record<string, unknown> {
    const t = totalsOf(p);
    return {
      __metadata: { type },
      Title: p.payoutRef,
      payoutRef: p.payoutRef,
      sewer: p.sewer.toUpperCase(),
      contractorId: p.contractorId,
      contractorName: p.contractorName,
      companyName: p.companyName ?? "",
      tin: p.tin ?? "",
      bir: p.bir,
      section: p.section ?? "",
      periodStart: p.periodStart || null,
      periodEnd: p.periodEnd || null,
      dateIssued: p.dateIssued || null,
      modeOfPayment: p.modeOfPayment,
      bankName: p.bankName ?? "",
      bankAccount: p.bankAccount ?? "",
      schedPayout: p.schedPayout ?? "",
      linesJson: JSON.stringify(p.lines),
      additionsJson: JSON.stringify(p.additions),
      deductionsJson: JSON.stringify(p.deductions),
      qtyTotal: t.qtyTotal,
      workTotal: t.workTotal,
      netAmount: t.netAmount,
      totalAmount: t.totalAmount,
      payoutStatus: p.status,
      remarks: p.remarks ?? "",
      createdBy: p.createdBy ?? "",
      createdAt: p.createdAt || null,
      issuedBy: p.issuedBy ?? "",
      issuedAt: p.issuedAt || null,
      paidBy: p.paidBy ?? "",
      paidAt: p.paidAt || null,
      voidedBy: p.voidedBy ?? "",
      voidedAt: p.voidedAt || null,
      voidReason: p.voidReason ?? "",
    };
  }

  public async createPayout(p: IPayout): Promise<IPayout> {
    const type = await this.type(SUBCON_PAYOUTS_LIST);
    const created = await this.sp.createItem<{ Id: number }>(SUBCON_PAYOUTS_LIST, this.payoutBody(p, type));
    return { ...p, spId: created?.Id };
  }

  /**
   * Bulk-create payouts the app did not itself issue — the RECORDS OF PAYOUT
   * / NON-BIR RECORDS import (payoutLedgerImport.ts).
   *
   * A BACKFILL, so it asks the guard first; iSyn2_SubconPayouts is on the
   * allow-list because nothing outside this module reads it. Rows go up a few
   * at a time — SharePoint throttles a flood, and a serial loop over two
   * thousand rows is a coffee break. `onProgress` reports done/total so the
   * screen can show it; `shouldStop` lets the person pull the plug, after
   * which what was written stays written (each row is its own item) and the
   * count says how far it got. Failures are collected, not thrown, so one bad
   * row does not abandon the other 1,999.
   */
  public async importPayouts(
    payouts: readonly IPayout[],
    onProgress?: (done: number, total: number) => void,
    shouldStop?: () => boolean,
  ): Promise<{ created: number; failed: { payoutRef: string; error: string }[]; stopped: boolean }> {
    assertBackfillTarget(SUBCON_PAYOUTS_LIST);
    const type = await this.type(SUBCON_PAYOUTS_LIST);
    const failed: { payoutRef: string; error: string }[] = [];
    let created = 0;
    let next = 0;
    let stopped = false;
    const total = payouts.length;
    const worker = async (): Promise<void> => {
      while (next < total) {
        if (shouldStop?.()) { stopped = true; return; }
        const p = payouts[next++];
        try {
          await this.sp.createItem<{ Id: number }>(SUBCON_PAYOUTS_LIST, this.payoutBody(p, type));
          created++;
        } catch (e) {
          failed.push({ payoutRef: p.payoutRef, error: e instanceof Error ? e.message : String(e) });
        }
        onProgress?.(created + failed.length, total);
      }
    };
    await Promise.all(Array.from({ length: Math.min(3, total) }, worker));
    return { created, failed, stopped };
  }

  public async updatePayout(spId: number, p: IPayout): Promise<void> {
    const type = await this.type(SUBCON_PAYOUTS_LIST);
    await this.sp.updateItem(SUBCON_PAYOUTS_LIST, spId, this.payoutBody(p, type));
  }

  /**
   * Move a payout to a new status, stamping who and when.
   *
   * The transition itself is checked by the caller against
   * `canTransition` — this only records it. Deliberately a narrow write
   * rather than a full save: marking forty payouts Paid should not rewrite
   * forty sets of lines, and a status click that re-serialised the whole row
   * would be a chance to lose an edit somebody else made meanwhile.
   */
  public async setPayoutStatus(
    spId: number, status: PayoutStatus, by: string, reason?: string
  ): Promise<void> {
    const type = await this.type(SUBCON_PAYOUTS_LIST);
    const now = new Date().toISOString();
    const body: Record<string, unknown> = { __metadata: { type }, payoutStatus: status };
    if (status === "Issued") { body.issuedBy = by; body.issuedAt = now; }
    if (status === "Paid") { body.paidBy = by; body.paidAt = now; }
    if (status === "Void") { body.voidedBy = by; body.voidedAt = now; body.voidReason = reason ?? ""; }
    await this.sp.updateItem(SUBCON_PAYOUTS_LIST, spId, body);
  }

  /**
   * Delete a payout row outright.
   *
   * Deliberately NOT how a payout is cancelled — that is Void, so the
   * reference keeps resolving to something. This is for a row that should
   * never have existed: a draft saved against the wrong week, before anyone
   * was handed a payslip for it. Refuses anything that is not a Draft,
   * because by the time a payslip has been issued the paper exists.
   */
  public async deleteDraft(payout: IPayout): Promise<void> {
    if (payout.status !== "Draft") {
      throw new Error(
        `Refusing to delete ${payout.payoutRef}: it is ${payout.status}, not a Draft. Void it instead — ` +
        `a reference somebody has been handed has to keep resolving to something.`);
    }
    if (!payout.spId) throw new Error("This draft has never been saved.");
    await this.sp.deleteItem(SUBCON_PAYOUTS_LIST, payout.spId);
  }

  // ---------------------------------------------------------------- settings

  /**
   * One module setting, or null.
   *
   * NEVER THROWS. The settings list arrived after the other three, so a
   * tenant provisioned before it simply does not have it — and the only
   * setting today is the payslip letterhead, which every payslip already
   * prints without. Turning "no logo yet" into an error would stop a payslip
   * that is otherwise perfectly correct from being printed at all.
   */
  private async readSetting(key: string): Promise<{ spId: number; value: string } | null> {
    try {
      const rows = await this.sp.getListItems<Record<string, unknown>>(SUBCON_SETTINGS_LIST, {
        select: SETTING_SELECT,
        filter: `settingKey eq '${key.replace(/'/g, "''")}'`,
        top: 1,
        singlePage: true,
      });
      const hit = rows[0];
      if (!hit) return null;
      return { spId: Number(hit.Id), value: text(hit.settingText) };
    } catch (e) {
      console.warn(`[SubconPayrollService] setting "${key}" unreadable — carrying on without it`, e);
      return null;
    }
  }

  private async writeSetting(key: string, value: string, by: string): Promise<void> {
    const type = await this.type(SUBCON_SETTINGS_LIST);
    const now = new Date().toISOString();
    const existing = await this.readSetting(key);
    const body = { __metadata: { type }, settingKey: key, settingText: value, updatedBy: by, updatedAt: now };
    if (existing) await this.sp.updateItem(SUBCON_SETTINGS_LIST, existing.spId, body);
    else await this.sp.createItem(SUBCON_SETTINGS_LIST, body);
  }

  /**
   * The payslip letterhead, as an `<img src>`, or null when none is set.
   *
   * Validated on the way OUT as well as on the way in: the value is a string
   * in a list a site owner can edit by hand, and a payslip is a document
   * somebody signs. Anything that is not a base64 image data URI is dropped
   * rather than rendered.
   */
  public async getPayslipLogo(): Promise<string | null> {
    const row = await this.readSetting(PAYSLIP_LOGO_KEY);
    if (!row || !row.value) return null;
    if (!isStorableLogo(row.value)) {
      console.warn("[SubconPayrollService] stored payslip logo is not a usable image data URI — ignoring it");
      return null;
    }
    return row.value;
  }

  /** Store the letterhead. Rejects before the round trip rather than after,
   *  because the failure a person can act on is "this file is too big", not
   *  SharePoint's own complaint about a field length. */
  public async setPayslipLogo(dataUrl: string, by: string): Promise<void> {
    if (!isStorableLogo(dataUrl)) {
      throw new Error(
        "That is not a storable logo. It has to be a PNG, JPEG, GIF, WebP or SVG image, small enough to " +
        "travel with every payslip."
      );
    }
    await this.writeSetting(PAYSLIP_LOGO_KEY, dataUrl, by);
  }

  /** Remove the letterhead. Payslips then print with the wordmark alone,
   *  which is what they did before one was ever uploaded. */
  public async clearPayslipLogo(by: string): Promise<void> {
    await this.writeSetting(PAYSLIP_LOGO_KEY, "", by);
  }

  /**
   * The Bizlink .xls template the bank file is written INTO (biffTemplate.ts),
   * stored base64 in the settings list. Null when none is uploaded — the
   * Bank File then builds a file from scratch and says so.
   */
  public async getBizlinkTemplate(): Promise<Uint8Array | null> {
    const row = await this.readSetting(BIZLINK_TEMPLATE_KEY);
    if (!row || !row.value) return null;
    try {
      const bytes = base64ToBytes(row.value);
      describeTemplate(bytes); // throws when it is not an .xls workbook
      return bytes;
    } catch (e) {
      console.warn("[SubconPayrollService] stored Bizlink template is not a readable .xls — ignoring it", e);
      return null;
    }
  }

  /** Store the template. Checked here so the person gets "not an .xls" rather
   *  than a field-length complaint from SharePoint. */
  public async setBizlinkTemplate(bytes: Uint8Array, by: string): Promise<{ sheets: number; strings: number }> {
    if (bytes.length > BIZLINK_TEMPLATE_MAX_BYTES) {
      throw new Error(`That file is ${Math.round(bytes.length / 1024)} KB; the Bizlink template is about 34 KB. Upload the FORMULA sheet saved as .xls, not the whole workbook.`);
    }
    const info = describeTemplate(bytes);
    if (info.sheets !== 1) throw new Error(`The template must have exactly one sheet; that file has ${info.sheets}.`);
    await this.writeSetting(BIZLINK_TEMPLATE_KEY, bytesToBase64(bytes), by);
    return info;
  }

  public async clearBizlinkTemplate(by: string): Promise<void> {
    await this.writeSetting(BIZLINK_TEMPLATE_KEY, "", by);
  }

  /** The payslip's controlled-document block — form code, revision,
   *  effective date. Defaults when nothing is stored or the list is unreadable. */
  public async getPayslipForm(): Promise<IPayslipForm> {
    const row = await this.readSetting(PAYSLIP_FORM_KEY);
    return parseStoredForm(row?.value);
  }

  public async setPayslipForm(form: IPayslipForm, by: string): Promise<void> {
    await this.writeSetting(PAYSLIP_FORM_KEY, JSON.stringify(form), by);
  }

  /**
   * Which PANTS band each design's trousers are paid under — the stored
   * choices only; pantsMap.ts layers them over the shipped evidence. Empty
   * when nothing has been chosen or the list is unreadable: the shipped map
   * then stands, which is what it is for.
   */
  public async getPantsMap(): Promise<StoredPantsMap> {
    const row = await this.readSetting(PANTS_MAP_KEY);
    return parseStoredPantsMap(row?.value);
  }

  /** Store one design's pants choice ("" for no pants line). Read-modify-
   *  write on the whole map: it is a few dozen entries, and a partial merge
   *  server-side is not something a note column can do. */
  public async setPantsChoice(design: string, pantsRateKey: string, by: string): Promise<StoredPantsMap> {
    const cur = await this.getPantsMap();
    const next = { ...cur, [design]: pantsRateKey };
    await this.writeSetting(PANTS_MAP_KEY, JSON.stringify(next), by);
    return next;
  }

  /** Forget a stored pants choice so the shipped map decides again. */
  public async clearPantsChoice(design: string, by: string): Promise<StoredPantsMap> {
    const cur = { ...(await this.getPantsMap()) };
    delete cur[design];
    await this.writeSetting(PANTS_MAP_KEY, JSON.stringify(cur), by);
    return cur;
  }

  /** Stored sewer aliases — sheet spelling → ledger key. See sewerAlias.ts. */
  public async getSewerAliases(): Promise<SewerAliases> {
    const row = await this.readSetting(SEWER_ALIASES_KEY);
    return parseStoredAliases(row?.value);
  }

  /** Record that a sheet spelling means this ledger key. */
  public async setSewerAlias(alias: string, ledgerKey: string, by: string): Promise<SewerAliases> {
    const cur = await this.getSewerAliases();
    const next = { ...cur, [alias.toUpperCase().trim()]: ledgerKey.toUpperCase().trim() };
    await this.writeSetting(SEWER_ALIASES_KEY, JSON.stringify(next), by);
    return next;
  }

  /**
   * WIP id → set / top only / pants only, as chosen by a person on the run.
   *
   * Only for WIPs the Cutting J.O. Log cannot answer for — everything cut
   * before WO Log importing began on 4 Sep 2026. See wipPlan.ts. Empty when
   * nothing has been chosen or the list is unreadable, which leaves the log
   * deciding and the rest assumed, and the run says how many.
   */
  public async getWipPlans(): Promise<StoredWipPlans> {
    const row = await this.readSetting(WIP_PLANS_KEY);
    return parseStoredWipPlans(row?.value);
  }

  /** Remember one WIP's plan. Read-modify-write on the whole map, the same
   *  way the pants choices and the sewer aliases are stored. */
  public async setWipPlan(wipId: string, plan: TagPlan, by: string): Promise<StoredWipPlans> {
    const cur = await this.getWipPlans();
    const next = { ...cur, [String(wipId).trim()]: plan };
    await this.writeSetting(WIP_PLANS_KEY, JSON.stringify(next), by);
    return next;
  }

  /** Forget a WIP's chosen plan, so the log or the assumption decides again. */
  public async clearWipPlan(wipId: string, by: string): Promise<StoredWipPlans> {
    const cur = { ...(await this.getWipPlans()) };
    delete cur[String(wipId).trim()];
    await this.writeSetting(WIP_PLANS_KEY, JSON.stringify(cur), by);
    return cur;
  }

  /**
   * Which TYPE of pants a WIP is, where a person said so — see wipPants.ts.
   * Empty means every WIP takes its design's pants band, as the run always did.
   */
  public async getWipPants(): Promise<StoredWipPants> {
    const row = await this.readSetting(WIP_PANTS_KEY);
    return parseStoredWipPants(row?.value);
  }

  /** Say what type of pants one WIP is (a PANTS rate key). Read-modify-write on
   *  the whole map, like the plans and the aliases. Answers with the new map. */
  public async setWipPants(wipId: string, rateKey: string, by: string): Promise<StoredWipPants> {
    const cur = await this.getWipPants();
    const next = { ...cur, [String(wipId).trim()]: String(rateKey).trim() };
    await this.writeSetting(WIP_PANTS_KEY, JSON.stringify(next), by);
    return next;
  }

  /** Forget a WIP's choice, so its design's pants band decides again. */
  public async clearWipPants(wipId: string, by: string): Promise<StoredWipPants> {
    const cur = { ...(await this.getWipPants()) };
    delete cur[String(wipId).trim()];
    await this.writeSetting(WIP_PANTS_KEY, JSON.stringify(cur), by);
    return cur;
  }

  /**
   * Hand-typed RATE and SETS on a payout's priced bands — see lineEdits.ts.
   * Empty means every payout is exactly what the run derived.
   */
  public async getLineEdits(): Promise<StoredLineEdits> {
    const row = await this.readSetting(LINE_EDITS_KEY);
    return parseStoredLineEdits(row?.value);
  }

  /** Record one band's typed figures (read-modify-write on the whole map).
   *  Answers with the whole new map. */
  public async setLineEdit(
    ref: string, derived: { rateKey: string; rate: number; qty: number },
    change: { rate?: number; qty?: number }, by: string,
  ): Promise<StoredLineEdits> {
    const cur = await this.getLineEdits();
    const next = mergeLineEdit(cur, String(ref).trim(), derived, change);
    await this.writeSetting(LINE_EDITS_KEY, JSON.stringify(next), by);
    return next;
  }

  /** Forget every edit on one payout — once it is paid, or on a reset. */
  public async clearLineEdits(ref: string, by: string): Promise<StoredLineEdits> {
    const cur = await this.getLineEdits();
    const next = clearPayoutEdits(cur, String(ref).trim());
    if (next !== cur) await this.writeSetting(LINE_EDITS_KEY, JSON.stringify(next), by);
    return next;
  }

  /**
   * The sets payable on a pass where a person said fewer than QC passed — see
   * payableQty.ts. Empty means every pass is paid at what QC passed.
   */
  public async getKeyPayable(): Promise<StoredKeyPayable> {
    const row = await this.readSetting(KEY_PAYABLE_KEY);
    return parseStoredKeyPayable(row?.value);
  }

  /** Write the payable for ONE row's passes in a single read-modify-write on
   *  the whole map (a row can be several passes; N parallel writes would race
   *  as `setKeyOverrides` explains). Answers with the whole new map. */
  public async setKeyPayable(
    passes: readonly IPayablePass[], alloc: Readonly<Record<string, number>>, by: string,
  ): Promise<StoredKeyPayable> {
    const cur = await this.getKeyPayable();
    const next = mergePayable(cur, passes, alloc);
    await this.writeSetting(KEY_PAYABLE_KEY, JSON.stringify(next), by);
    return next;
  }

  /** What BizLink's Pay non-BPI file needs per person — see nonBpiDetails.ts. */
  public async getNonBpiDetails(): Promise<StoredNonBpiDetails> {
    const row = await this.readSetting(NON_BPI_DETAILS_KEY);
    return parseNonBpiDetails(row?.value);
  }

  public async setNonBpiDetail(ledgerKey: string, patch: INonBpiDetail, by: string): Promise<StoredNonBpiDetails> {
    const cur = await this.getNonBpiDetails();
    const next = mergeNonBpiDetail(cur, ledgerKey, patch);
    await this.writeSetting(NON_BPI_DETAILS_KEY, JSON.stringify(next), by);
    return next;
  }

  /** Many people's details in ONE read-modify-write (a bulk entry must not race itself). */
  public async setNonBpiDetails(items: readonly { key: string; detail: INonBpiDetail }[], by: string): Promise<StoredNonBpiDetails> {
    let cur = await this.getNonBpiDetails();
    for (const it of items) cur = mergeNonBpiDetail(cur, it.key, it.detail);
    await this.writeSetting(NON_BPI_DETAILS_KEY, JSON.stringify(cur), by);
    return cur;
  }

  public async getNonBpiDefaults(): Promise<INonBpiDefaults> {
    const row = await this.readSetting(NON_BPI_DEFAULTS_KEY);
    return parseNonBpiDefaults(row?.value);
  }

  public async setNonBpiDefaults(d: INonBpiDefaults, by: string): Promise<INonBpiDefaults> {
    await this.writeSetting(NON_BPI_DEFAULTS_KEY, JSON.stringify(d), by);
    return d;
  }

  /**
   * The passes somebody has taken off a run, or put on it — see
   * keyOverrides.ts. Empty when nothing has been touched, which is the run
   * exactly as BQC5 derives it.
   */
  public async getKeyOverrides(): Promise<StoredKeyOverrides> {
    const row = await this.readSetting(KEY_OVERRIDES_KEY);
    return parseStoredKeyOverrides(row?.value);
  }

  /** Hold one pass off the run, or put one on it. Read-modify-write on the
   *  whole map, the same way the plans and the aliases are stored. */
  public async setKeyOverride(key: string, override: KeyOverride, by: string): Promise<StoredKeyOverrides> {
    const cur = await this.getKeyOverrides();
    const next = { ...cur, [String(key).trim()]: override };
    await this.writeSetting(KEY_OVERRIDES_KEY, JSON.stringify(next), by);
    return next;
  }

  /** Put a pass back under the run's own judgement — neither held nor added. */
  public async clearKeyOverride(key: string, by: string): Promise<StoredKeyOverrides> {
    const cur = { ...(await this.getKeyOverrides()) };
    delete cur[String(key).trim()];
    await this.writeSetting(KEY_OVERRIDES_KEY, JSON.stringify(cur), by);
    return cur;
  }

  /**
   * The same as `setKeyOverride`, for several keys at once — ONE
   * read-modify-write, not one per key. A bulk hold ticking N rows and
   * calling `setKeyOverride` N times in parallel would have each call read
   * the SAME "before" map and race to write it back, so only the last one
   * landing would survive — every other tick silently lost. Sequential calls
   * would be correct but cost N round trips for what is one edit.
   */
  public async setKeyOverrides(keys: readonly string[], override: KeyOverride, by: string): Promise<StoredKeyOverrides> {
    const cur = await this.getKeyOverrides();
    const next = { ...cur };
    for (const k of keys) { const t = String(k).trim(); if (t) next[t] = override; }
    await this.writeSetting(KEY_OVERRIDES_KEY, JSON.stringify(next), by);
    return next;
  }

  /** The same as `clearKeyOverride`, for several keys at once — see
   *  `setKeyOverrides` for why this must not be a loop of single calls. */
  public async clearKeyOverrides(keys: readonly string[], by: string): Promise<StoredKeyOverrides> {
    const cur = { ...(await this.getKeyOverrides()) };
    for (const k of keys) delete cur[String(k).trim()];
    await this.writeSetting(KEY_OVERRIDES_KEY, JSON.stringify(cur), by);
    return cur;
  }

  /**
   * Event keys the OLD system's own workbook already paid — see
   * workbookPaidKeys.ts for why this is chunked rather than one settings row.
   *
   * ONE FILTERED READ, NOT A COUNT PLUS N PARALLEL LOOKUPS. The original
   * shape read the count row, then fired one `settingKey eq '...'` request per
   * chunk (12 of them on the real file) — 13 separate round trips every time
   * the payroll screen loads, which is most of what made "settings" the
   * slowest thing on the loading screen next to the slip list. `settingKey`
   * is single-line Text and already indexed (provisioned that way from the
   * start, for exactly this), so `startswith(settingKey, 'workbookPaidKeys:')`
   * gets every chunk row in ONE request — the index makes the prefix scan
   * cheap, and the chunk count (well under SharePoint's 5,000-row page) means
   * it is one page, not several.
   *
   * STILL SAFE TO READ MID-WRITE. `addWorkbookPaidKeys` rewrites every chunk
   * from the merged whole, but `mergeKeys` keeps existing keys in their
   * existing order and only appends new ones at the end — so every chunk
   * below the old count is BYTE-IDENTICAL before and after a capture except
   * the last, and a chunk row beyond the old count simply does not exist
   * until the writer creates it. A scan that lands mid-write therefore still
   * returns a valid, merely-slightly-behind set: at worst it misses the keys
   * just added, never a corrupted mix — the same fail-open direction as
   * before, just without the count row as the guard.
   *
   * A chunk that will not parse, or the whole filtered read failing, is
   * treated as "nothing captured yet", which is the state it was in before
   * this existed and can only cost a WIP being priced again, never the other
   * way round — the same fail-open direction every settings blob in this
   * module takes.
   */
  public async getWorkbookPaidKeys(): Promise<Set<string>> {
    const rows = await this.sp.getListItems<Record<string, unknown>>(SUBCON_SETTINGS_LIST, {
      select: SETTING_SELECT,
      filter: `startswith(settingKey,'${WORKBOOK_PAID_KEYS_PREFIX}')`,
    }).catch(() => [] as Record<string, unknown>[]);
    const out = new Set<string>();
    for (const r of rows) for (const k of parseKeyChunk(text(r.settingText))) out.add(k);
    return out;
  }

  /**
   * Union `newKeys` into what is already captured and rewrite every chunk.
   *
   * READ-MODIFY-WRITE ON THE WHOLE SET, not a patch to one chunk — a key can
   * land in any chunk once everything is re-sorted, and rewriting only the
   * changed chunk would need to know which one that is. This runs rarely (a
   * deliberate click after reviewing the AT-RISK list), so the extra writes
   * cost nothing that matters. THE COUNT IS WRITTEN LAST, after every chunk
   * has been confirmed written — a read midway through sees the OLD, still-
   * complete set rather than a half-updated one, and a write that fails
   * partway leaves the count pointing at the last chunk layout that was
   * fully there.
   */
  public async addWorkbookPaidKeys(
    newKeys: readonly string[], by: string,
  ): Promise<{ added: number; total: number }> {
    const before = await this.getWorkbookPaidKeys();
    const merged = mergeKeys(before, newKeys);
    const chunks = chunkKeys(merged);
    for (let i = 0; i < chunks.length; i++) {
      await this.writeSetting(`${WORKBOOK_PAID_KEYS_PREFIX}${i}`, JSON.stringify(chunks[i]), by);
    }
    await this.writeSetting(WORKBOOK_PAID_KEYS_COUNT_KEY, String(chunks.length), by);
    return { added: merged.length - before.size, total: merged.length };
  }

  /**
   * Un-capture keys — the ONLY delete path, and a deliberate click: the
   * workbook's own flag now says these are not paid (FOR PAYROLL / blank).
   * Chunks past the new layout are overwritten with [] rather than left, or
   * the prefix read would resurrect their old keys.
   */
  public async removeWorkbookPaidKeys(
    keys: readonly string[], by: string,
  ): Promise<{ removed: number; total: number }> {
    const before = await this.getWorkbookPaidKeys();
    const drop = new Set(keys);
    const kept = [...before].filter(k => !drop.has(k));
    const oldChunks = chunkKeys([...before]).length;
    const chunks = chunkKeys(kept);
    for (let i = 0; i < Math.max(chunks.length, oldChunks); i++) {
      await this.writeSetting(`${WORKBOOK_PAID_KEYS_PREFIX}${i}`, JSON.stringify(chunks[i] ?? []), by);
    }
    await this.writeSetting(WORKBOOK_PAID_KEYS_COUNT_KEY, String(chunks.length), by);
    return { removed: before.size - kept.length, total: kept.length };
  }
}

const liveSubconPayrollService = new SubconPayrollService();

/**
 * The one door into payroll's data, and the one place the demo switches.
 *
 * WHY A PROXY RATHER THAN A BRANCH IN EVERY METHOD. There are thirty-odd of
 * them and thirty call sites; a branch in each is thirty chances to forget
 * one, and the one you forget writes invented money into a real list. Here the
 * live class is untouched — the demo cannot change how production behaves,
 * because production's code never learns the demo exists.
 *
 * FAIL CLOSED, NOT OPEN. In demo mode a method the store implements runs
 * against memory. A method it does not implement falls through to the live
 * service ONLY if it is a read: reading the real payslip letterhead makes a
 * demo payslip look like the real thing and can damage nothing. A WRITE it
 * does not implement THROWS, because the alternative is a banner saying "demo"
 * over a call that just changed a payout somebody is owed.
 */
export const subconPayrollService: SubconPayrollService = new Proxy(liveSubconPayrollService, {
  get(target, prop, receiver) {
    if (typeof prop === "string" && isDemo()) {
      const store = demoPayrollStore as unknown as Record<string, unknown>;
      const fn = store[prop];
      if (typeof fn === "function") return (fn as (...a: unknown[]) => unknown).bind(store);
      if (PAYROLL_WRITE_METHODS.has(prop)) {
        return () => Promise.reject(new Error(
          `${prop} is not available while payroll is in demo mode. Leave the demo on Payroll Settings first.`
        ));
      }
    }
    return Reflect.get(target, prop, receiver) as unknown;
  },
});
